import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Star, ExternalLink, Quote } from 'lucide-react';
import { useSite } from '@/context/SiteContext';
import { SectionHeading } from './Motion';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const PREVIEW_CHARS = 260;
const INTERVAL_MS = 7000;

const sourceStyle = {
  TripAdvisor: 'text-[#34E0A1] border-[#34E0A1]/40',
  Google: 'text-[#4285F4] border-[#4285F4]/40',
  'Booking.com': 'text-[#003580] border-[#003580]/30',
  Expedia: 'text-[#FFCC00] border-[#d4a900]/50',
  Agoda: 'text-[#5392F9] border-[#5392F9]/40',
  Airbnb: 'text-[#FF5A5F] border-[#FF5A5F]/40',
  Traveloka: 'text-[#1BA0E2] border-[#1BA0E2]/40',
  'Tiket.com': 'text-[#0064D2] border-[#0064D2]/40',
};

export const Stars = ({ rating, scale = 5, size = 'h-3.5 w-3.5' }) => {
  const normalized = scale === 10 ? rating / 2 : rating;
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${rating} out of ${scale}`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={`${size} ${n <= Math.round(normalized) ? 'fill-[hsl(var(--brand-sun))] text-[hsl(var(--brand-sun))]' : 'text-border'}`} />
      ))}
    </span>
  );
};

const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d + (d.length === 10 ? 'T00:00:00' : ''));
  return Number.isNaN(dt.getTime()) ? d : dt.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
};

/**
 * Guest reviews as a fading carousel, like the hero: one review at a time, auto-rotating, looping,
 * pausing on hover. Long reviews are previewed with a "Read more" dialog so the home page stays compact.
 */
export const ReviewsSection = ({ reviews = [] }) => {
  const { site } = useSite();
  const reduce = useReducedMotion();
  const s = site?.settings || {};
  const platforms = [
    ['TripAdvisor', s.tripadvisor_url], ['Google', s.google_reviews_url], ['Booking.com', s.booking_url],
    ['Agoda', s.agoda_url], ['Airbnb', s.airbnb_url], ['Expedia', s.expedia_url], ['Traveloka', s.traveloka_url],
  ].filter(([, url]) => url);

  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [openReview, setOpenReview] = useState(null);
  const count = reviews.length;

  const go = useCallback((d) => setIdx((i) => (count ? (i + d + count) % count : 0)), [count]);

  useEffect(() => { setIdx(0); }, [count]);
  useEffect(() => {
    if (count < 2 || paused || openReview || reduce) return undefined;
    const t = setInterval(() => go(1), INTERVAL_MS);
    return () => clearInterval(t);
  }, [count, paused, openReview, reduce, go]);

  // Only approved reviews reach the public API. Hide the whole section until at least one is published.
  if (!count) return null;

  const avg = reviews.reduce((a, r) => a + (r.rating_scale === 10 ? r.rating / 2 : r.rating), 0) / count;
  const r = reviews[Math.min(idx, count - 1)];
  const long = (r.text || '').length > PREVIEW_CHARS;
  const preview = long ? `${r.text.slice(0, PREVIEW_CHARS).replace(/\s+\S*$/, '')}…` : r.text;

  return (
    <section className="section-y bg-secondary/60" data-testid="reviews-section" id="reviews">
      <div className="container-x">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <SectionHeading label="Guest reviews" title="Words from the hill" />
          <div className="flex items-center gap-6">
            <div className="text-right" data-testid="reviews-average">
              <p className="font-display text-3xl leading-none">{avg.toFixed(1)}<span className="text-base text-muted-foreground">/5</span></p>
              <p className="text-[11px] tracking-[0.14em] uppercase text-muted-foreground mt-1">{count} guest review{count === 1 ? '' : 's'}</p>
            </div>
            {count > 1 && (
              <div className="hidden md:flex gap-2">
                <button type="button" onClick={() => go(-1)} aria-label="Previous review" data-testid="reviews-prev-button" className="h-10 w-10 rounded-full border border-border inline-flex items-center justify-center text-foreground/70 hover:text-foreground hover:bg-card transition-colors"><ArrowLeft className="h-4 w-4" /></button>
                <button type="button" onClick={() => go(1)} aria-label="Next review" data-testid="reviews-next-button" className="h-10 w-10 rounded-full border border-border inline-flex items-center justify-center text-foreground/70 hover:text-foreground hover:bg-card transition-colors"><ArrowRight className="h-4 w-4" /></button>
              </div>
            )}
          </div>
        </div>

        {/* Fading spotlight, one review at a time */}
        <div
          className="relative mt-10 rounded-2xl bg-card border border-border shadow-[var(--shadow-soft)] overflow-hidden"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          data-testid="reviews-carousel"
          data-index={idx}
          data-count={count}
        >
          <Quote className="absolute -top-3 -left-2 h-28 w-28 text-[hsl(var(--brand-gold))]/10 rotate-180" aria-hidden="true" />
          <div className="relative min-h-[300px] sm:min-h-[260px]">
            <AnimatePresence mode="wait" initial={false}>
              <motion.article
                key={r.id}
                initial={{ opacity: 0, y: reduce ? 0 : 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: reduce ? 0 : -10 }}
                transition={{ duration: 0.7, ease: 'easeInOut' }}
                className="p-7 sm:p-10 lg:p-12 flex flex-col"
                data-testid="review-card"
                aria-live="polite"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <Stars rating={r.rating} scale={r.rating_scale} size="h-4 w-4" />
                    <span className="text-xs text-muted-foreground" data-testid="review-rating">{r.rating}/{r.rating_scale}</span>
                  </div>
                  <span className={`text-[10px] tracking-[0.18em] uppercase border rounded-full px-2.5 py-1 ${sourceStyle[r.source] || 'text-muted-foreground border-border'}`} data-testid="review-source">{r.source}</span>
                </div>
                {r.title && <h3 className="font-display text-xl sm:text-2xl mt-6" data-testid="review-title">{r.title}</h3>}
                <p className="mt-5 font-display text-lg sm:text-xl lg:text-[1.35rem] leading-[1.65] text-foreground/85 max-w-3xl" data-testid="review-text">&ldquo;{preview}&rdquo;</p>
                {long && (
                  <button type="button" onClick={() => setOpenReview(r)} className="mt-4 self-start eyebrow text-[hsl(var(--brand-teal))] link-underline inline-flex items-center gap-1.5" data-testid="review-read-more">
                    Read more <ArrowRight className="h-3 w-3" />
                  </button>
                )}
                <footer className="mt-8 pt-5 border-t border-border flex items-center justify-between text-xs">
                  <span className="font-medium" data-testid="review-author">{r.author}{r.country ? <span className="text-muted-foreground font-normal"> &middot; {r.country}</span> : null}</span>
                  {r.date && <time className="text-muted-foreground">{fmtDate(r.date)}</time>}
                </footer>
              </motion.article>
            </AnimatePresence>
          </div>

          {count > 1 && (
            <div className="flex items-center justify-between gap-4 px-7 sm:px-10 lg:px-12 pb-6">
              {count <= 10 ? (
                <div className="flex gap-2" data-testid="reviews-dots">
                  {reviews.map((x, i) => (
                    <button type="button" key={x.id} onClick={() => setIdx(i)} aria-label={`Go to review ${i + 1}`} className={`h-[3px] rounded-full transition-[width,background-color] duration-500 ${i === idx ? 'w-10 bg-foreground' : 'w-5 bg-border hover:bg-foreground/40'}`} />
                  ))}
                </div>
              ) : (
                <span className="text-[11px] tracking-[0.2em] text-muted-foreground tabular-nums" data-testid="reviews-counter">{String(idx + 1).padStart(2, '0')} / {String(count).padStart(2, '0')}</span>
              )}
              <div className="flex md:hidden gap-2">
                <button type="button" onClick={() => go(-1)} aria-label="Previous review" data-testid="reviews-prev-button-mobile" className="h-10 w-10 rounded-full border border-border inline-flex items-center justify-center text-foreground/70"><ArrowLeft className="h-4 w-4" /></button>
                <button type="button" onClick={() => go(1)} aria-label="Next review" data-testid="reviews-next-button-mobile" className="h-10 w-10 rounded-full border border-border inline-flex items-center justify-center text-foreground/70"><ArrowRight className="h-4 w-4" /></button>
              </div>
            </div>
          )}
        </div>

        <Dialog open={!!openReview} onOpenChange={(o) => !o && setOpenReview(null)}>
          <DialogContent className="max-w-lg bg-card max-h-[85vh] overflow-y-auto" data-testid="review-dialog">
            {openReview && (
              <>
                <DialogHeader>
                  <div className="flex items-center justify-between gap-3 pr-6">
                    <div className="flex items-center gap-2">
                      <Stars rating={openReview.rating} scale={openReview.rating_scale} />
                      <span className="text-xs text-muted-foreground">{openReview.rating}/{openReview.rating_scale}</span>
                    </div>
                    <span className={`text-[10px] tracking-[0.18em] uppercase border rounded-full px-2.5 py-1 ${sourceStyle[openReview.source] || 'text-muted-foreground border-border'}`}>{openReview.source}</span>
                  </div>
                  <DialogTitle className="font-display text-2xl font-normal mt-3 text-left">{openReview.title || `A stay remembered by ${openReview.author}`}</DialogTitle>
                  <DialogDescription className="text-left">
                    {openReview.author}{openReview.country ? ` · ${openReview.country}` : ''}{openReview.date ? ` · ${fmtDate(openReview.date)}` : ''}
                  </DialogDescription>
                </DialogHeader>
                <p className="text-[15px] leading-[1.85] text-foreground/85 whitespace-pre-line" data-testid="review-dialog-text">&ldquo;{openReview.text}&rdquo;</p>
              </>
            )}
          </DialogContent>
        </Dialog>

        {platforms.length > 0 && (
          <div className="mt-10 pt-8 border-t border-border flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8" data-testid="reviews-platforms">
            <p className="eyebrow text-muted-foreground shrink-0">Read more on</p>
            <div className="flex flex-wrap gap-2">
              {platforms.map(([name, url]) => (
                <a key={name} href={url} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-1.5 text-[11px] tracking-[0.16em] uppercase border rounded-full px-3.5 py-1.5 bg-card hover:bg-background transition-colors ${sourceStyle[name] || 'border-border text-foreground/70'}`} data-testid={`review-platform-${name.toLowerCase().replace(/[^a-z]/g, '')}`}>
                  {name} <ExternalLink className="h-3 w-3 opacity-60" />
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
